import { generateGid, arrayChunk, delay } from "@theme/global-utilities";

/**
 * GraphQLClient – minimal resilient GraphQL fetcher for Shopify Storefront API
 * ---------------------------------------------------------------------------
 * Responsibilities:
 *  - Send POST requests to Storefront API with query + variables
 *  - Handle timeouts, retries, and 429 "Too Many Requests"
 *  - Return a consistent result shape { ok, data, error }
 */

/**
 * @template T
 * @typedef {object} GraphQLResponse
 * @property {boolean} ok - True if request succeeded and no GraphQL errors.
 * @property {T|null} data - Parsed GraphQL data (or null on failure).
 * @property {Error|null} error - Error object if failed, else null.
 */
export class GraphQLClient {
  /**
   * @param {object} opts
   * @param {string} opts.endpoint - Full Storefront GraphQL endpoint URL.
   * @param {string} opts.token - Storefront API access token.
   * @param {number} [opts.timeoutMs=15000] - Request timeout in ms.
   * @param {number} [opts.maxRetries=3] - Number of retry attempts on network/HTTP errors.
   */
  constructor({ endpoint, token, timeoutMs = 15000, maxRetries = 3 }) {
    this.endpoint = endpoint;
    this.token = token;
    this.timeoutMs = timeoutMs;
    this.maxRetries = maxRetries;
  }

  /**
   * Execute a GraphQL query with variables.
   *
   * @template T
   * @param {string} query - GraphQL query string.
   * @param {object} [variables={}] - Variables for the query.
   * @returns {Promise<GraphQLResponse<T>>}
   */
  async fetch(query, variables = {}) {
    let attempt = 0;

    while (true) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeoutMs);

      try {
        const res = await fetch(this.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": this.token,
          },
          body: JSON.stringify({ query, variables }),
          signal: controller.signal,
        });

        clearTimeout(timer);

        // Handle 429 rate limit
        if (res.status === 429 && attempt < this.maxRetries) {
          const retryAfter =
            Number(res.headers.get("Retry-After")) * 1000 ||
            this._backoff(attempt);
          await delay(retryAfter);
          attempt++;
          continue;
        }

        const payload = await res.json().catch(() => ({}));
        const { data, errors } = payload || {};

        if (!res.ok || errors) {
          const message = errors
            ? /** @type {{ message: string }[]} */ (errors)
                .map(({ message }) => message)
                .join(", ")
            : `HTTP ${res.status}`;
          if (attempt < this.maxRetries) {
            await delay(this._backoff(attempt));
            attempt++;
            continue;
          }
          return { ok: false, data: null, error: new Error(message) };
        }

        return { ok: true, data, error: null };
      } catch (err) {
        clearTimeout(timer);
        if (attempt < this.maxRetries) {
          await delay(this._backoff(attempt));
          attempt++;
          continue;
        }
        return {
          ok: false,
          data: null,
          error: err instanceof Error ? err : new Error(String(err)),
        };
      }
    }
  }

  /**
   * Compute exponential backoff delay in milliseconds.
   *
   * @private
   * @param {number} attempt - Retry attempt index (0-based).
   * @param {number} [base=500] - Base delay in ms for the first attempt.
   * @returns {number} Milliseconds to wait before next retry.
   */
  _backoff(attempt, base = 500) {
    // Exponential backoff with cap (0.5s - ~32s)
    return base * 2 ** Math.min(attempt, 6);
  }
}

/**
 * ChunkedProductsService
 * -----------------------
 * Fetches Shopify Product nodes by ID/GID in batched GraphQL calls with:
 *  - In-memory cache per GID (prevents re-fetching)
 *  - In-flight de-duplication (multiple callers share the same request)
 *  - Chunking (limits batch size per network round trip)
 *
 * This class is transport-agnostic: you pass a GraphQL client with a .fetch(query, variables) method
 * that resolves to { ok: boolean, data?: any, error?: Error } (as in your GraphQLClient).
 *
 * Public API:
 *   - constructor(client, { chunkSize = 25 } = {})
 *   - fetchInChunks(query, ids): Promise<{ byId: Map<string,any>, list: any[], errors?: Record<string,string> }>
 *   - clear(): void
 *   - evict(id: string|number): void
 *   - preload(ids, query): void  // fire-and-forget
 */
export class ChunkedProductsService {
  /**
   * @param {{ fetch: <T=any>(query: string, variables?: object) => Promise<GraphQLResponse<T>> }} client
   * A GraphQL client implementing the above signature.
   *
   * @param {object} [opts]
   * @param {number} [opts.chunkSize=25] - Max number of IDs per network chunk (tune for your query’s variable layout).
   */
  constructor(client, { chunkSize = 25 } = {}) {
    /** @private */
    this.client = client;

    /** @private */
    this.chunkSize = Math.max(1, chunkSize | 0);

    /**
     * Product cache keyed by Product GID.
     * @type {Map<string, any>}
     * @private
     */
    this.cache = new Map();

    /**
     * In-flight map keyed by Product GID -> { promise, resolve, reject }.
     * Ensures concurrent callers share the same network result.
     * @type {Map<string, { promise: Promise<any>, resolve: (v:any)=>void, reject: (e:any)=>void }>}
     * @private
     */
    this.inFlight = new Map();
  }

  /**
   * Remove all cached entries and pending in-flight trackers.
   * Safe to call at any time (e.g., after deploys or when invalidating data).
   */
  clear() {
    this.cache.clear();
    this.inFlight.clear();
  }

  /**
   * Remove a single product from cache (by numeric ID or GID).
   *
   * @param {string|number} id - Numeric product ID or full GID.
   */
  evict(id) {
    const gid = generateGid(id);
    if (gid) this.cache.delete(gid);
  }

  /**
   * Preload a set of IDs into cache without awaiting results.
   * Useful for warming cache based on anticipation (e.g., hover or viewport heuristics).
   *
   * @param {Array<string|number>} ids
   * @param {string} query
   */
  preload(ids, query) {
    // Fire-and-forget; errors are intentionally swallowed here
    this.fetchInChunks(query, ids).catch(() => {});
  }

  /**
   * Main API: Fetch products by IDs in chunks with cache and in-flight de-duplication.
   *
   * @param {string} query
   * A GraphQL document that can fetch multiple products by ID using `nodes(ids: [ID!]!)`.
   *
   * @param {Array<string|number>} ids
   * Array of numeric IDs or GIDs. Order is preserved in the returned `list` (for items actually found).
   *
   * @returns {Promise<{ byId: Map<string, any>, list: any[], errors?: Record<string, string> }>}
   * - byId: Map of GID -> product for fast lookups
   * - list: Products in the same order as the input `ids` (excluding misses)
   * - errors (optional): Record of GID -> error message for any IDs that failed or were not found
   */
  async fetchInChunks(query, ids) {
    // Normalize & short-circuit
    const normalized = this._normalizeIds(ids);
    if (normalized.length === 0) {
      return { byId: new Map(), list: [] };
    }

    // Schedule work: figure out which IDs need network and what promises we should await
    const unique = this._unique(normalized);
    const { toFetch, waiters } = this._schedule(unique);

    // Execute network in chunks; collect per-GID errors
    const errors = Object.create(null);
    const chunks = arrayChunk(toFetch, this.chunkSize);

    await Promise.all(
      chunks.map((chunk) => this._processChunk({ query, chunk, errors }))
    );

    // Ensure every in-flight waiter resolves/rejects before building return value
    await Promise.allSettled(waiters);

    // Build return shape (byId + list in input order) and attach errors if any
    return this._collectResult(normalized, errors);
  }

  /**
   * Convert input ids to GIDs and drop invalid entries.
   *
   * @private
   * @param {Array<string|number>} ids
   * @returns {string[]} GIDs only
   */
  _normalizeIds(ids) {
    return ids.map(generateGid).filter(Boolean);
  }

  /**
   * Return a de-duplicated shallow copy (preserves first occurrence order).
   *
   * @private
   * @param {string[]} gids
   * @returns {string[]}
   */
  _unique(gids) {
    const seen = new Set();
    const out = [];
    for (const gid of gids) {
      if (!seen.has(gid)) {
        seen.add(gid);
        out.push(gid);
      }
    }
    return out;
  }

  /**
   * Decide which GIDs require a network request and which promises to await.
   * - If cached: no action
   * - If already in-flight: reuse its promise
   * - Else: create a new deferred and mark as in-flight
   *
   * @private
   * @param {string[]} gids - Unique, normalized GIDs
   * @returns {{ toFetch: string[], waiters: Promise<any>[] }}
   */
  _schedule(gids) {
    const toFetch = [];
    const waiters = [];

    for (const gid of gids) {
      // Cache hit — nothing to load and nothing to await
      if (this.cache.has(gid)) continue;

      // Already in-flight — join
      const existing = this.inFlight.get(gid);
      if (existing) {
        waiters.push(existing.promise);
        continue;
      }

      // New work — create deferred and mark in-flight
      const d = this._defer();
      this.inFlight.set(gid, d);
      toFetch.push(gid);
      waiters.push(d.promise);
    }

    return { toFetch, waiters };
  }

  /**
   * Execute one network chunk and resolve/reject per-GID deferreds accordingly.
   * Always removes in-flight entries for GIDs in this chunk.
   *
   * @private
   * @param {object} params
   * @param {string} params.query
   * @param {string[]} params.chunk
   * @param {Record<string, string>} params.errors
   */
  async _processChunk({ query, chunk, errors }) {
    if (!chunk.length) return;

    // nodes(ids:[ID!]!)
    const res = await this.client.fetch(query, { ids: chunk });
    const { ok, data, error } = res || {};

    if (!ok || !data) {
      // Fail the entire chunk
      const errMsg = error?.message || "Request failed";
      for (const gid of chunk) {
        errors[gid] = errMsg;
        this._rejectInFlight(gid, error || new Error(errMsg));
      }
      return;
    }

    // Map<GID, product> from data.nodes
    const byId = new Map();
    const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
    for (const n of nodes) {
      const id = n && typeof n.id === "string" ? n.id : null;
      if (id) byId.set(id, n);
    }

    // Resolve each GID in this batch
    for (const gid of chunk) {
      const node = byId.get(gid);
      if (node) {
        this.cache.set(gid, node);
        this._resolveInFlight(gid, node);
      } else {
        const err = new Error(`Product not found: ${gid}`);
        errors[gid] = err.message;
        this._rejectInFlight(gid, err);
      }
    }
  }

  /**
   * Build final payload:
   *  - byId: Map of GID -> product
   *  - list: products in the same order as the original input (skips misses)
   *  - errors?: included if any occurred
   *
   * @private
   * @param {string[]} normalizedOrder
   * @param {Record<string, string>} errors
   */
  _collectResult(normalizedOrder, errors) {
    const byId = new Map();
    const list = [];

    for (const gid of normalizedOrder) {
      const p = this.cache.get(gid);
      if (p) {
        if (!byId.has(gid)) byId.set(gid, p);
        list.push(p);
      }
    }

    return Object.keys(errors).length ? { byId, list, errors } : { byId, list };
  }

  /**
   * Resolve and remove an in-flight entry for a given GID.
   *
   * @private
   * @param {string} gid
   * @param {any} value
   */
  _resolveInFlight(gid, value) {
    const d = this.inFlight.get(gid);
    if (d?.resolve) d.resolve(value);
    this.inFlight.delete(gid);
  }

  /**
   * Reject and remove an in-flight entry for a given GID.
   *
   * @private
   * @param {string} gid
   * @param {any} err
   */
  _rejectInFlight(gid, err) {
    const d = this.inFlight.get(gid);
    if (d?.reject) d.reject(err);
    this.inFlight.delete(gid);
  }

  /**
   * Tiny deferred builder
   *
   * @private
   * @returns {{ promise: Promise<any>, resolve: (v:any)=>void, reject: (e:any)=>void }}
   */
  _defer() {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    // @ts-ignore – the variables are set by the executor above
    return { promise, resolve, reject };
  }
}

/**
 * RequestAccumulator
 * ------------------
 * Batches incoming ID requests within a short delay window (`flushDelay`).
 * Each batch triggers a single `loaderFn(ids)` call, and all callers
 * in that window share the same result. New calls after flush join
 * the next batch.
 *
 * @template T
 * @class
 * @param {(ids: string[]) => Promise<T>} loaderFn - Function called once per batch with unique IDs.
 * @param {{ flushDelay?: number }} [opts] - Delay (ms) before flushing the batch.
 */
class RequestAccumulator {
  /**
   * @param {(ids: string[]) => Promise<{ byId: Map<string, any>, list: any[], errors?: Record<string,string> }>} loaderFn
   * @param {{ flushDelay?: number }} [opts]
   */
  constructor(loaderFn, { flushDelay = 0 } = {}) {
    this.loaderFn = loaderFn;
    this.flushDelay = flushDelay;

    this._bucket = new Set(); // IDs for the currently scheduled batch
    this._timer = null; // timer for the current batch
    this._current = null; // { promise, resolve, reject } for the current batch
  }

  /**
   * @param {string|number} id
   * @returns {string|null}
   */
  _toGid(id) {
    if (typeof id === "string" && id.startsWith("gid://")) return id;
    const g = generateGid?.(id);
    return typeof g === "string" ? g : null;
  }

  /**
 * @template T
 * @returns {{ promise: Promise<T>, resolve: (value: T) => void, reject: (reason?: any) => void }}
 */
  _defer() {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    // @ts-ignore
    return { promise, resolve, reject };
  }

  /**
   * Add IDs to the currently scheduled batch and get that batch's result.
   * @param {Array<string|number>} ids
   * @returns {Promise<any>}
   */
  add(ids) {
    // If no batch is scheduled yet, create one now
    if (!this._current) {
      this._current = this._defer();
      this._timer = setTimeout(() => this._flushNow(), this.flushDelay);
    }

    // Add incoming IDs to the current bucket
    for (const id of ids) {
      const gid = this._toGid(id);
      if (gid) this._bucket.add(gid);
    }

    // Return the promise for THIS batch (even if caller added nothing)
    return this._current.promise;
  }

  async _flushNow() {
    // Snapshot the current batch and clear scheduling state
    const ids = Array.from(this._bucket);
    this._bucket.clear();

    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }

    const batch = this._current; // the deferred for this batch
    this._current = null; // allow a new batch to start collecting immediately

    // If nothing to load, resolve with an empty result
    if (ids.length === 0) {
      batch?.resolve?.({ byId: new Map(), list: [] });
      return;
    }

    try {
      const res = await this.loaderFn(ids);
      batch?.resolve?.(res);
    } catch (e) {
      batch?.reject?.(e);
    }
  }

  /**
   * Optional manual flush trigger; if a batch is scheduled, flush it now.
   */
  flush() {
    if (this._timer != null) {
      clearTimeout(this._timer);
      this._timer = null;

      Promise.resolve().then(() => this._flushNow());
    }
  }
}

/**
 * createStorefrontClient
 * Minimal factory for a Storefront GraphQL client.
 *
 * Auto-detects endpoint/token unless you override.
 *
 * @param {object} [opts]
 * @param {string} [opts.apiVersion='2025-04']
 * @param {number} [opts.timeoutMs=15000]
 * @param {number} [opts.maxRetries=3]
 * @param {string} [opts.endpoint]
 * @param {string} [opts.token]
 * @returns {GraphQLClient}
 */
export const createStorefrontClient = function createStorefrontClient({
  apiVersion = "2025-04",
  timeoutMs = 15000,
  maxRetries = 3,
  endpoint,
  token,
} = {}) {
  const shop =
    (typeof Shopify !== "undefined" && Shopify.shop) ||
    (window.Shopify && window.Shopify.shop);

  const resolvedEndpoint =
    endpoint || `https://${shop}/api/${apiVersion}/graphql.json`;
  const resolvedToken =
    token || window.shopifyVariables.STOREFRONT_ACCESS_TOKEN;

  if (!resolvedEndpoint || !resolvedToken) {
    throw new Error("[createStorefrontClient] Missing endpoint or token");
  }

  return new GraphQLClient({
    endpoint: resolvedEndpoint,
    token: resolvedToken,
    timeoutMs,
    maxRetries,
  });
};

/**
 * createNodesChunkedProductsService
 * ---------------------------------
 * Lightweight factory for a nodes(ids:[ID!]!) product batching service.
 *
 * You provide:
 *  - query: string (must accept `$ids: [ID!]!` and return `nodes { ... on Product { ... } }`)
 *
 * @param {object} opts
 * @param {string} opts.query - GraphQL query using `nodes(ids:[ID!]!)`
 * @param {number} [opts.chunkSize=10] - Max number of IDs per chunk
 * @param {string} [opts.apiVersion='2025-04'] - Storefront API version (if no client passed)
 * @param {GraphQLClient} [opts.client] - Custom GraphQL client (optional)
 * @param {number} [opts.flushDelay=0] - Accumulator flush delay in ms (0 = same tick)
 * @returns {{
 *   fetchProductsInChunks(ids: Array<string|number>): Promise<{ byId: Map<string, any>, list: any[], errors?: Record<string,string> }>,
 *   fetchProductsBatched(ids: Array<string|number>): Promise<{ byId: Map<string, any>, list: any[], errors?: Record<string,string> }>,
 *   getAccumulator(): RequestAccumulator<{ byId: Map<string, any>, list: any[], errors?: Record<string,string> }>,
 *   service: ChunkedProductsService,
 *   query: string
 * }}
 */
export const createNodesChunkedProductsService =
  function createNodesChunkedProductsService({
    query,
    chunkSize = 10,
    apiVersion = "2025-04",
    client,
    flushDelay = 0,
  }) {
    if (!query)
      throw new Error("[createNodesChunkedProductsService] query required");

    const gqlClient = client || createStorefrontClient({ apiVersion });

    const service = new ChunkedProductsService(gqlClient, { chunkSize });

    // Bridge accumulator - service.fetchInChunks
    /** @type {(ids: string[]) => Promise<{ byId: Map<string, any>, list: any[], errors?: Record<string,string> }>} */
    const loader = (ids) => service.fetchInChunks(query, ids);

    const accumulator = new RequestAccumulator(loader, { flushDelay });

    return {
      /**
       * Fetch products by ids using nodes(ids:[ID!]!)
       * @param {Array<string|number>} ids
       */
      async fetchProductsInChunks(ids) {
        return service.fetchInChunks(query, ids);
      },

      /**
       * Accumulated fetch: merges concurrent/same-tick calls into one request.
       * @param {Array<string|number>} ids
       */
      async fetchProductsBatched(ids) {
        return accumulator.add(ids);
      },

      /**
       * Access the underlying accumulator (e.g., to call .flush()).
       */
      getAccumulator() {
        return accumulator;
      },

      service,
      query,
    };
};
